<template>
  <section class="hero-section" :style="heroStyles">
    <div v-if="carouselEnabled && slides.length > 0" class="hero-slider">
      <div
        v-for="(slide, index) in slides"
        :key="index"
        class="hero-slide"
        :class="{ active: currentSlide === index }"
        :style="{ backgroundImage: `url(${slide})` }"
      />
    </div>

    <div class="hero-content">
      <div class="hero-text-container">
        <h1 class="hero-title">{{ title }}</h1>
        <p class="hero-subtitle">{{ subtitle }}</p>

        <div v-if="button1 || button2" class="hero-ctas">
          <router-link v-if="button1" :to="button1.path" :class="['btn', `btn-${button1.variant || 'primary'}`]">
            {{ button1.label }}
          </router-link>

          <router-link v-if="button2" :to="button2.path" :class="['btn', `btn-${button2.variant || 'secondary'}`]">
            {{ button2.label }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Slider Controls (Only show if carousel enabled and multiple slides) -->
    <div v-if="carouselEnabled && slides.length > 1" class="slider-controls">
      <button
        v-for="(_, index) in slides"
        :key="index"
        class="slider-dot"
        :class="{ active: currentSlide === index }"
        @click="currentSlide = index"
        :aria-label="`Slide ${index + 1}`"
      />
    </div>
  </section>
</template>

<script>
export default {
  name: 'Hero',
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      required: true
    },
    button1: {
      type: Object,
      default: null
      // Expected: { label: String, path: String, variant: 'primary'|'secondary' }
    },
    button2: {
      type: Object,
      default: null
      // Expected: { label: String, path: String, variant: 'primary'|'secondary' }
    },
    carouselEnabled: {
      type: Boolean,
      default: false
    },
    slides: {
      type: Array,
      default: () => []
      // Array of image URLs
    },
    slideInterval: {
      type: Number,
      default: 5000
      // Time between slide changes in milliseconds
    },
    backgroundColor: {
      type: String,
      default: 'linear-gradient(135deg, #38624c 0%, #538554 100%)'
    },
    textColor: {
      type: String,
      default: '#ffffff'
    }
  },
  data() {
    return {
      currentSlide: 0,
      slideTimer: null
    };
  },
  computed: {
    heroStyles() {
      return {
        background: this.backgroundColor,
        color: this.textColor
      };
    }
  },
  watch: {
    currentSlide() {
      // Reset timer when slide is manually changed
      if (this.slideTimer) {
        clearInterval(this.slideTimer);
        this.startSlideshow();
      }
    }
  },
  mounted() {
    if (this.carouselEnabled && this.slides.length > 0) {
      this.startSlideshow();
    }
  },
  beforeUnmount() {
    this.stopSlideshow();
  },
  methods: {
    startSlideshow() {
      if (this.slides.length === 0) return;

      this.slideTimer = setInterval(() => {
        this.currentSlide = (this.currentSlide + 1) % this.slides.length;
      }, this.slideInterval);
    },

    stopSlideshow() {
      if (this.slideTimer) {
        clearInterval(this.slideTimer);
        this.slideTimer = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/_variables.scss';

/* Hero Section */
.hero-section {
  position: relative;
  color: white;
  padding: 6rem 2rem;
  overflow: hidden;
  min-height: 600px;
  display: flex;
  align-items: center;
  background-size: cover;
  background-position: center;
  background-attachment: fixed;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
    min-height: 500px;
  }

  @media (max-width: 480px) {
    padding: 3rem 1rem;
    min-height: 400px;
  }
}

.hero-slider {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.hero-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  opacity: 0;
  transition: opacity 0.8s ease-in-out;

  &.active {
    opacity: 1;
  }
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  z-index: 2;
  padding: 0 2rem;

  @media (max-width: 968px) {
    justify-content: center;
  }

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
}

.hero-text-container {
  background: rgba(0, 0, 0, 0.55);
  padding: 4rem;
  border-radius: 12px;
  max-width: 600px;
  animation: slideInLeft 0.8s ease-out;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    max-width: 95%;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
}

.hero-subtitle {
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2.5rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
}

.hero-ctas {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;

    .btn {
      width: 100%;
    }
  }
}

/* Buttons */
.btn {
  display: inline-block;
  padding: 12px 32px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;

  &.btn-primary {
    background: $brand-color;
    color: white;

    &:hover {
      background: $brand-dark;
      transform: translateY(-2px);
      box-shadow: 0 12px 24px rgba($brand-color, 0.2);
    }
  }

  &.btn-secondary {
    background: transparent;
    color: white;
    border: 2px solid white;

    &:hover {
      background: $brand-color;
      border-color: $brand-color;
    }
  }

  &.btn-lg {
    padding: 16px 48px;
    font-size: 1.1rem;
  }
}

/* Slider Controls */
.slider-controls {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
  z-index: 3;

  @media (max-width: 768px) {
    bottom: 20px;
    gap: 10px;
  }
}

.slider-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.8);
    transform: scale(1.2);
  }

  &.active {
    background: white;
    box-shadow: 0 0 8px rgba(255, 255, 255, 0.8);
  }

  @media (max-width: 768px) {
    width: 10px;
    height: 10px;
  }
}

/* Animations */
@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
